import { safeExecute, } from "@opendaw/lib-std";
import { Box, NoPointers, PointerField, StringField, BooleanField, Float32Field, Int32Field, ArrayField, } from "@opendaw/lib-box";
import { CubedPattern } from "./CubedPattern";
import { Pointers } from "@opendaw/studio-enums";
export class CubedDeviceBox extends Box {
    static create(graph, uuid, constructor) {
        return graph.stageBox(new CubedDeviceBox({
            uuid,
            graph,
            name: "CubedDeviceBox",
            pointerRules: {
                accepts: [
                    Pointers.Device,
                    Pointers.Selection,
                    Pointers.MetaData,
                    Pointers.SideChain,
                ],
                mandatory: false,
                exclusive: false,
            },
            resource: undefined,
            ephemeral: false,
        }), constructor);
    }
    static ClassName = "CubedDeviceBox";
    static Resource = undefined;
    static Ephemeral = false;
    static Tags = Object.freeze({
        type: "device",
        deviceType: "instrument",
        content: "notes",
    });
    constructor(construct) {
        super(construct);
    }
    accept(visitor) {
        return safeExecute(visitor.visitCubedDeviceBox, this);
    }
    get tags() {
        return CubedDeviceBox.Tags;
    }
    get host() {
        return this.getField(1);
    }
    get label() {
        return this.getField(2);
    }
    get icon() {
        return this.getField(3);
    }
    get enabled() {
        return this.getField(4);
    }
    get minimized() {
        return this.getField(5);
    }
    get tuning() {
        return this.getField(10);
    }
    get cutoff() {
        return this.getField(11);
    }
    get resonance() {
        return this.getField(12);
    }
    get envMod() {
        return this.getField(13);
    }
    get decay() {
        return this.getField(14);
    }
    get accent() {
        return this.getField(15);
    }
    get volume() {
        return this.getField(16);
    }
    get waveform() {
        return this.getField(17);
    }
    get patternIndex() {
        return this.getField(20);
    }
    get patterns() {
        return this.getField(30);
    }
    get version() {
        return this.getField(99);
    }
    initializeFields() {
        return {
            1: PointerField.create({
                parent: this,
                fieldKey: 1,
                fieldName: "host",
                deprecated: false,
                pointerRules: NoPointers,
            }, Pointers.InstrumentHost, true),
            2: StringField.create({
                parent: this,
                fieldKey: 2,
                fieldName: "label",
                deprecated: false,
                pointerRules: NoPointers,
            }),
            3: StringField.create({
                parent: this,
                fieldKey: 3,
                fieldName: "icon",
                deprecated: false,
                pointerRules: NoPointers,
            }),
            4: BooleanField.create({
                parent: this,
                fieldKey: 4,
                fieldName: "enabled",
                deprecated: false,
                pointerRules: NoPointers,
            }, true),
            5: BooleanField.create({
                parent: this,
                fieldKey: 5,
                fieldName: "minimized",
                deprecated: false,
                pointerRules: NoPointers,
            }, false),
            10: Float32Field.create({
                parent: this,
                fieldKey: 10,
                fieldName: "tuning",
                deprecated: false,
                pointerRules: {
                    accepts: [
                        Pointers.Modulation,
                        Pointers.Automation,
                        Pointers.MIDIControl,
                    ],
                    mandatory: false,
                    exclusive: false,
                },
            }, { min: -1200, max: 1200, scaling: "linear" }, "ct", 0),
            11: Float32Field.create({
                parent: this,
                fieldKey: 11,
                fieldName: "cutoff",
                deprecated: false,
                pointerRules: {
                    accepts: [
                        Pointers.Modulation,
                        Pointers.Automation,
                        Pointers.MIDIControl,
                    ],
                    mandatory: false,
                    exclusive: false,
                },
            }, "unipolar", "%", 0),
            12: Float32Field.create({
                parent: this,
                fieldKey: 12,
                fieldName: "resonance",
                deprecated: false,
                pointerRules: {
                    accepts: [
                        Pointers.Modulation,
                        Pointers.Automation,
                        Pointers.MIDIControl,
                    ],
                    mandatory: false,
                    exclusive: false,
                },
            }, "unipolar", "%", 1),
            13: Float32Field.create({
                parent: this,
                fieldKey: 13,
                fieldName: "envMod",
                deprecated: false,
                pointerRules: {
                    accepts: [
                        Pointers.Modulation,
                        Pointers.Automation,
                        Pointers.MIDIControl,
                    ],
                    mandatory: false,
                    exclusive: false,
                },
            }, "unipolar", "%", 1),
            14: Float32Field.create({
                parent: this,
                fieldKey: 14,
                fieldName: "decay",
                deprecated: false,
                pointerRules: {
                    accepts: [
                        Pointers.Modulation,
                        Pointers.Automation,
                        Pointers.MIDIControl,
                    ],
                    mandatory: false,
                    exclusive: false,
                },
            }, "unipolar", "%", 0.5),
            15: Float32Field.create({
                parent: this,
                fieldKey: 15,
                fieldName: "accent",
                deprecated: false,
                pointerRules: {
                    accepts: [
                        Pointers.Modulation,
                        Pointers.Automation,
                        Pointers.MIDIControl,
                    ],
                    mandatory: false,
                    exclusive: false,
                },
            }, "unipolar", "%", 1),
            16: Float32Field.create({
                parent: this,
                fieldKey: 16,
                fieldName: "volume",
                deprecated: false,
                pointerRules: {
                    accepts: [
                        Pointers.Modulation,
                        Pointers.Automation,
                        Pointers.MIDIControl,
                    ],
                    mandatory: false,
                    exclusive: false,
                },
            }, "decibel", "dB", -12),
            17: Int32Field.create({
                parent: this,
                fieldKey: 17,
                fieldName: "waveform",
                deprecated: false,
                pointerRules: {
                    accepts: [
                        Pointers.Modulation,
                        Pointers.Automation,
                        Pointers.MIDIControl,
                    ],
                    mandatory: false,
                    exclusive: false,
                },
            }, { values: [0, 1] }, "", 0),
            20: Int32Field.create({
                parent: this,
                fieldKey: 20,
                fieldName: "patternIndex",
                deprecated: false,
                pointerRules: {
                    accepts: [
                        Pointers.Modulation,
                        Pointers.Automation,
                        Pointers.MIDIControl,
                    ],
                    mandatory: false,
                    exclusive: false,
                },
            }, { min: 0, max: 15 }, "", 0),
            30: ArrayField.create({
                parent: this,
                fieldKey: 30,
                fieldName: "patterns",
                deprecated: false,
                pointerRules: NoPointers,
            }, (construct) => CubedPattern.create(construct), 16),
            99: Int32Field.create({
                parent: this,
                fieldKey: 99,
                fieldName: "version",
                deprecated: false,
                pointerRules: NoPointers,
            }, "any", ""),
        };
    }
}
